import {
  call,
  put,
  takeLatest,
} from 'redux-saga/effects';

import api from '../../api/axiosConfig';

import {
  AdminUserRow,
  AdminUsersQuery,
  AdminUsersResponse,
  fetchAdminUsersFailure,
  fetchAdminUsersRequest,
  fetchAdminUsersSuccess,
} from '../slice/adminUsersSlice';

type AdminUserRowApi = {
  userId?: number | string | null;

  email?: string | null;
  emailMasked?: string | null;

  createdAt?: string | null;

  role?: string | null;
  isActive?: boolean | null;

  plan?: string | null;

  subscriptionStatus?: string | null;
  nextBillingDate?: string | null;

  telegramLinked?: boolean | null;
};

type AdminUsersResponseApi = {
  items?: AdminUserRowApi[] | null;

  total?: number | null;

  page?: number | null;
  size?: number | null;
  pageCount?: number | null;
};

function getErrorMessage(
    error: any,
): string {
  const status =
      error?.response?.status;

  if (status === 400) {
    return (
        error?.response?.data?.message ||
        '조회 조건이 올바르지 않습니다.'
    );
  }

  if (status === 401) {
    return '로그인이 필요합니다.';
  }

  if (status === 403) {
    return '운영자 권한이 없습니다.';
  }

  if (status === 404) {
    return '운영자 고객 조회 API가 연결되지 않았습니다.';
  }

  return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      '고객 정보 조회에 실패했습니다.'
  );
}

function normalizeAdminUserRow(
    row: AdminUserRowApi,
): AdminUserRow {
  return {
    userId:
        row.userId ?? '',

    emailMasked:
        row.emailMasked ||
        row.email ||
        '-',

    email:
        row.email ||
        undefined,

    createdAt:
        row.createdAt ||
        '',

    role:
        row.role ||
        'USER',

    isActive:
        row.isActive !== false,

    plan:
        row.plan ||
        'FREE',

    subscriptionStatus:
        row.subscriptionStatus ||
        'free',

    nextBillingDate:
        row.nextBillingDate ??
        null,

    telegramLinked:
        row.telegramLinked === true,
  };
}

function normalizeAdminUsersResponse(
    payload: AdminUsersResponseApi,
    request: AdminUsersQuery,
): AdminUsersResponse {
  const items =
      Array.isArray(payload.items)
          ? payload.items.map(
              normalizeAdminUserRow,
          )
          : [];

  const rawTotal =
      Number(
          payload.total ??
          items.length,
      );

  const total =
      Number.isFinite(rawTotal) &&
      rawTotal >= 0
          ? rawTotal
          : items.length;

  const rawPage =
      Number(
          payload.page ??
          request.page,
      );

  const page =
      Number.isFinite(rawPage) &&
      rawPage > 0
          ? Math.floor(rawPage)
          : request.page;

  const rawSize =
      Number(
          payload.size ??
          request.size,
      );

  const size =
      Number.isFinite(rawSize) &&
      rawSize > 0
          ? Math.floor(rawSize)
          : request.size;

  const calculatedPageCount =
      total > 0
          ? Math.ceil(
              total /
              Math.max(1, size),
          )
          : 1;

  const rawPageCount =
      Number(
          payload.pageCount ??
          calculatedPageCount,
      );

  const pageCount =
      Number.isFinite(rawPageCount) &&
      rawPageCount > 0
          ? Math.floor(rawPageCount)
          : calculatedPageCount;

  return {
    items,
    total,
    page,
    size,
    pageCount,
  };
}

function* fetchAdminUsersSaga(
    action: ReturnType<
        typeof fetchAdminUsersRequest
    >,
): Generator<any, void, any> {
  debugger;
  try {
    const {
      q,
      status,
      page,
      size,
    } = action.payload;

    const params:
        Record<
            string,
            string | number
        > = {
      page,
      size,
    };

    const normalizedQ =
        String(q ?? '').trim();

    if (normalizedQ) {
      params.q =
          normalizedQ;
    }

    if (
        status &&
        status !== 'all'
    ) {
      params.status =
          status;
    }

    /*
     * axiosConfig baseURL=/api
     *
     * Backend:
     * @RequestMapping("/api/user")
     * @GetMapping("/admin/users")
     *
     * 실제 요청:
     * GET /api/user/admin/users
     */
    const response =
        yield call(
            api.get,
            '/user/admin/users',
            {
              params,
            },
        );

    const normalized =
        normalizeAdminUsersResponse(
            response?.data ?? {},
            action.payload,
        );

    yield put(
        fetchAdminUsersSuccess(
            normalized,
        ),
    );
  } catch (error: any) {
    yield put(
        fetchAdminUsersFailure(
            getErrorMessage(
                error,
            ),
        ),
    );
  }
}

export default function* adminUsersSaga() {
  yield takeLatest(
      fetchAdminUsersRequest.type,
      fetchAdminUsersSaga,
  );
}
